import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Messages } from "../../../layout/Messages/Messages";
import { onEnter, onExit } from "../../../../config/customModalFunctions";
import { NOTIFICATION_CHANGE_PACKAGE_OWNER_MODAL } from "../../../../config/notifications";
import {
  getEligiblePackageOwners,
  transferPackageOwner
} from "../../../../api/clients/packages";

function ChangeOwnerModal(props) {
  const { showModal, closeModal, item, actionCallback } = props;
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const intl = useIntl();

  const currentOwner = item?.user;

  useEffect(() => {
    if (!showModal || !item?.uuid) {
      return;
    }

    setSearch("");
    setSelectedUserId(null);
    setLoading(true);

    getEligiblePackageOwners({
      packageId: item.uuid,
      notificationId: NOTIFICATION_CHANGE_PACKAGE_OWNER_MODAL,
      hideNotification: true,
    })
      .then((response) => {
        const eligibleUsers = response?.data ?? [];
        const currentOwnerEntry = currentOwner?.id ? [{
          ...currentOwner,
          isCurrentOwner: true,
          disabled: true,
        }] : [];

        setUsers([
          ...currentOwnerEntry,
          ...eligibleUsers.map((user) => ({
            ...user,
            disabled: false,
          })),
        ]);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [showModal, item, currentOwner]);

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
    const email = (user.email || "").toLowerCase();
    const searchTerm = search.toLowerCase();

    return user.disabled || fullName.includes(searchTerm) || email.includes(searchTerm);
  });

  const handleSave = () => {
    if (!selectedUserId) {
      return;
    }

    setSaving(true);

    transferPackageOwner({
      packageId: item.uuid,
      newUserId: selectedUserId,
      notificationId: NOTIFICATION_CHANGE_PACKAGE_OWNER_MODAL,
    })
      .then((response) => {
        if (response?.data?.id || response?.data?.uuid) {
          if (actionCallback) {
            actionCallback(true);
          }
          closeModal();
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      isOpen={showModal}
      toggle={closeModal}
      size="md"
      onEnter={onEnter}
      onExit={onExit}
      centered
      returnFocusAfterClose={false}
    >
      <ModalHeader className="pb-0" toggle={closeModal}>
        <FormattedMessage id="components.pages.change_owner.modal.title" />
      </ModalHeader>

      <ModalBody>
        <Messages notificationId={NOTIFICATION_CHANGE_PACKAGE_OWNER_MODAL} />

        <p className="text-muted small mb-2">
          <FormattedMessage id="components.pages.change_owner.modal.description" />
        </p>

        <div className="justify-content-start align-items-center mb-4 mt-4 d-flex w-100">
          <FontAwesomeIcon size="lg" className="ml-2 text-muted" icon={faSearch} />
          <input
            className="form-control form-control-sm mx-2"
            type="text"
            maxLength="150"
            placeholder={intl.formatMessage({
              id: "components.layout.paginator.searchTable.searchText",
            })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </div>

        <ListGroup className="list change-owner-list">
          {!loading && filteredUsers.length === 0 && (
            <ListGroupItem className="custom-list-item">
              <FormattedMessage id="messages.no_data" />
            </ListGroupItem>
          )}
          {!loading && filteredUsers.map((user) => {
            const isSelected = selectedUserId === user.id;

            return (
              <ListGroupItem
                key={user.id}
                className={`custom-list-item${user.disabled ? " disabled text-muted" : " cursor-pointer"}${isSelected ? " active" : ""}`}
                onClick={user.disabled ? undefined : () => setSelectedUserId(user.id)}
                action={!user.disabled}
              >
                <div className="d-flex align-items-center justify-content-between w-100 change-owner-list__item-content">
                  <div className="change-owner-list__name">
                    <div className={`font-weight-bold${user.disabled ? " text-muted" : ""}`}>
                      {user.first_name} {user.last_name}
                      {user.isCurrentOwner && (
                        <span className="badge badge-secondary ml-2 change-owner-list__badge">
                          <FormattedMessage id="components.pages.change_owner.modal.current_owner_badge" />
                        </span>
                      )}
                    </div>
                    {user.email && (
                      <div className="small text-muted">
                        {user.email}
                      </div>
                    )}
                  </div>
                  {isSelected && !user.disabled && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="ml-3 flex-shrink-0"
                      size="lg"
                    />
                  )}
                </div>
              </ListGroupItem>
            );
          })}
        </ListGroup>
      </ModalBody>

      <ModalFooter>
        <div className="text-center text-sm-right py-3 custom-disabled-buttons-modal">
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saving || !selectedUserId}
          >
            <FormattedMessage id="button.save" />
          </Button>
        </div>
        <Button color="secondary" onClick={closeModal} disabled={saving}>
          <FormattedMessage id="button.cancel" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { ChangeOwnerModal };
